import { StyledInputField } from "@/components/fundraising/advanced-search/AdvancedSearchFilters";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import CloseSVG from "@/images/close-icon.svg";
import { SmallLazoTeamUser } from "@/models/SmallLazoTeamUser";
import { DEFAULT_LAZO_TEAM_FILTERS } from "@/pages/LazoTeamPage";
import LazoTeamService from "@/services/LazoTeamService";
import { Box, MenuItem, MenuList, Popper, Typography, styled } from "@mui/material";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { SuggestionItem } from "../SuggestionItem";
import { PortalFilters } from "./PortalChatContext";

const debouncer = new Debouncer(150);

type Props = {
  setFilters: Dispatch<SetStateAction<PortalFilters>>;
  selectedEmployees: SmallLazoTeamUser[];
  setSelectedEmployees: Dispatch<SetStateAction<SmallLazoTeamUser[]>>;
};

export function PortalEmployeesFilterSelector({ setFilters, selectedEmployees, setSelectedEmployees }: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  const [employees, setEmployees] = useState<SmallLazoTeamUser[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [menuAnchor, setMenuAnchor] = useState<HTMLInputElement | null>(null);

  const popperRef = useRef<HTMLDivElement | null>(null);

  function onItemClick(employee: SmallLazoTeamUser) {
    const idx = selectedEmployees.findIndex((e) => e.coreUserExternalId === employee.coreUserExternalId);
    if (idx === -1) {
      const updatedEmployees = selectedEmployees.concat(employee);
      setSelectedEmployees(updatedEmployees);
      setFilters((prev) => ({ ...prev, employeesExternalIds: updatedEmployees.map((e) => e.coreUserExternalId) }));
    } else {
      const updatedEmployees = selectedEmployees.filter((e) => e.coreUserExternalId !== employee.coreUserExternalId);
      setSelectedEmployees(updatedEmployees);
      setFilters((prev) => ({ ...prev, employeesExternalIds: updatedEmployees.map((e) => e.coreUserExternalId) }));
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.value !== "" && !menuAnchor) {
      setMenuAnchor(e.target);
    } else if (e.target.value === "" && menuAnchor) {
      setMenuAnchor(null);
      setInputValue("");
      return;
    }

    setInputValue(e.target.value);

    debouncer.executeLastCall(() => {
      setLoading(true);
      LazoTeamService.getLazoTeamPaged(0, 20, { ...DEFAULT_LAZO_TEAM_FILTERS, search: inputValue })
        .then((r) => {
          r.onSuccess((c) => {
            setEmployees(c.content);
          });
          r.onError((m) => {
            NotificationService.error(m);
          });
        })
        .finally(() => setLoading(false));
    });
  }

  function onFocus(e: React.FocusEvent<HTMLInputElement>) {
    setMenuAnchor(e.target);
  }

  return (
    <>
      <MainContainer>
        <SelectedListContainer display="flex" flexDirection="column">
          {selectedEmployees.length === 0 && (
            <Box padding="0.5rem">
              <Typography>No employees selected</Typography>
            </Box>
          )}
          {selectedEmployees.map((e) => (
            <SuggestionItem
              boxProps={{ onClick: () => onItemClick(e) }}
              containerSx={{
                backgroundColor: "transparent",
                cursor: "pointer",
                padding: "0.5rem",
                ":hover": {
                  backgroundColor: Colors.ACCENT_RED_DESATURATED_FFEAEA,
                },
              }}
              entry={{
                id: e.coreUserExternalId,
                value: e.firstName + " " + e.lastName,
                email: e.email,
                lazoEmployee: true,
              }}
            />
          ))}
        </SelectedListContainer>
        <StyledInputField
          InputProps={{
            endAdornment: (
              <>
                {inputValue.trim() !== "" && (
                  <img
                    className="close-icon-search"
                    src={CloseSVG.src}
                    alt="close-icon"
                    onClick={() => {
                      onInputChange({ target: { value: "" } } as any);
                      setMenuAnchor(null);
                    }}
                  />
                )}
              </>
            ),
          }}
          value={inputValue}
          hasValue={inputValue !== ""}
          onChange={onInputChange}
          onFocus={onFocus}
          onKeyDown={(ev) => {
            if (ev.key === "Escape") {
              // @ts-expect-error
              ev.target.blur();
              setMenuAnchor(null);
              setInputValue("");
            }
          }}
          color={Colors.LAZO_PURPLE}
          autoComplete="off"
          placeHolder="Search"
          sx={{
            width: "25rem",
          }}
        />
      </MainContainer>
      <Popper
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        disablePortal={true}
        placement={"bottom-start"}
        ref={popperRef}
        sx={{ zIndex: 1200 }}
      >
        <StyledMenuList>
          {loading ? (
            <Box>
              <Spinner size="1rem" sx={{ color: Colors.LAZO_PURPLE }} />
            </Box>
          ) : employees.length === 0 ? (
            <Typography>No matches</Typography>
          ) : (
            employees.map((e, idx) => (
              <MenuItem
                key={idx}
                onClick={() => {
                  onItemClick(e);
                  onInputChange({ target: { value: "" } } as any);
                }}
              >
                <SuggestionItem
                  containerSx={{ backgroundColor: "transparent" }}
                  entry={{
                    id: e.coreUserExternalId,
                    value: e.firstName + " " + e.lastName,
                    email: e.email,
                    lazoEmployee: true,
                  }}
                />
              </MenuItem>
            ))
          )}
        </StyledMenuList>
      </Popper>
    </>
  );
}

const StyledMenuList = styled(MenuList)(() => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  backgroundColor: "#fff",
  borderRadius: "0.5rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  minWidth: "20rem",
  boxShadow: "0 0 19px 0 rgba(0, 0, 0, 0.1)",
  maxHeight: "40vh",
  overflow: "hidden",
  overflowY: "auto",
  "& .MuiMenuItem-root": {
    width: "100%",
  },
}));

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
}));

const SelectedListContainer = styled(Box)(() => ({
  backgroundColor: "white",
  borderRadius: "0.5rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  maxHeight: "30vh",
  overflow: "hidden",
  overflowY: "auto",
}));
